import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Button, Form, Table, Alert, Spinner, Badge, Tabs, Tab, Modal, FloatingLabel, InputGroup } from 'react-bootstrap';
import { get, post } from '../../services/apiController';
import moment from 'moment';
import { toast, ToastContainer } from 'react-toastify';
import PaginationInPage from '../Pagination';
import { FiUpload, FiDownload, FiRefreshCw, FiSearch } from 'react-icons/fi';
import { saveAs } from 'file-saver';

const IOCLReport = () => {
  const [loading, setLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [importLogs, setImportLogs] = useState([]);
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [stats, setStats] = useState({
    totalTransactions: 0,
    transactionsByType: [],
    latestSync: null
  });
  const [dateRange, setDateRange] = useState({
    fromDate: moment().subtract(6, 'days').format('YYYY-MM-DD'),
    toDate: moment().format('YYYY-MM-DD')
  });
  const [activeTab, setActiveTab] = useState('transactions');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const TABLE_DEFAULT_LIMIT = 50;
  const [transactionDate, setTransactionDate] = useState(moment().format('YYYY-MM-DD'));
  const [vehicleNumber, setVehicleNumber] = useState('');
  const [excelLoading, setExcelLoading] = useState(false);

  // Fetch transactions and stats on component mount
  useEffect(() => {
    if (activeTab === 'transactions') {
      fetchTransactions();
      fetchStats();
    }
    if (activeTab === 'imports') {
      fetchImportLogs();
    }
  }, [activeTab, currentPage]);

  // Fetch transactions from database
  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const params = [];
      if (transactionDate) params.push(`date=${transactionDate}`);
      if (vehicleNumber) params.push(`vehicle_number=${encodeURIComponent(vehicleNumber)}`);
      params.push(`page=${currentPage}`);
      params.push(`limit=${TABLE_DEFAULT_LIMIT}`);

      const response = await get(`/vm/iocl/transactions?${params.join('&')}`);
      if (response?.success && response?.data) {
        setTransactions(response.data.rows || []);
        setTotalRecords(response.data.count || 0);
      } else {
        setTransactions([]);
        setTotalRecords(0);
        if (response?.message) {
          toast.error(`Error fetching transactions: ${response.message}`);
        }
      }
    } catch (error) {
      console.error('Error fetching IOCL transactions:', error);
      toast.error(`Failed to fetch transactions: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  // Fetch IOCL transaction statistics
  const fetchStats = async () => {
    try {
      const response = await get('/vm/iocl/stats');

      if (response?.success && response?.data) {
        setStats(response.data);
      } else if (response?.message) {
        toast.error(`Error fetching stats: ${response.message}`);
      }
    } catch (error) {
      console.error('Error fetching IOCL transaction stats:', error);
      toast.error(`Failed to fetch stats: ${error.message || 'Unknown error'}`);
    }
  };

  // Fetch import logs
  const fetchImportLogs = async () => {
    try {
      const response = await get(`/vm/daily-report-import/logs?page=${currentPage}&limit=${TABLE_DEFAULT_LIMIT}`);

      if (response?.success && response?.data?.logs) {
        setImportLogs(response.data.logs);
        setTotalRecords(response.data.pagination?.total || 0);
      } else if (response?.message) {
        toast.error(`Error fetching import logs: ${response.message}`);
      }
    } catch (error) {
      console.error('Error fetching import logs:', error);
      toast.error(`Failed to fetch import logs: ${error.message || 'Unknown error'}`);
    }
  };

  // Manually trigger IOCL transaction sync
  const triggerSync = async () => {
    setSyncLoading(true);
    try {
      // Convert dates to YYYY-MM-DD format for the API
      const fromDate = moment(dateRange.fromDate).format('YYYY-MM-DD');
      const toDate = moment(dateRange.toDate).format('YYYY-MM-DD');

      const response = await post({}, `/vm/iocl/trigger-sync?fromDate=${fromDate}&toDate=${toDate}`);

      if (response?.success) {
        toast.success('IOCL transaction sync completed successfully');
        fetchTransactions(); // Refresh transactions
        fetchStats(); // Refresh stats
      } else {
        toast.error(`Sync failed: ${response?.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error triggering IOCL transaction sync:', error);
      toast.error(`Sync failed: ${error.message || 'Unknown error'}`);
    } finally {
      setSyncLoading(false);
    }
  };

  // Handle file selection for import
  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0]);
  };

  // Handle file import
  const handleImport = async () => {
    if (!selectedFile) {
      toast.error('Please select a file to import');
      return;
    }
    setImportLoading(true);
    const formData = new FormData();
    formData.append('vehicleFile', selectedFile);
    post(formData, '/vm/daily-report-import/upload', false).then(res => {
      console.log(res);
      toast.success('File imported successfully');
      setShowImportModal(false);
      setSelectedFile(null);
      fetchImportLogs();
      fetchTransactions();
    }).catch(err => {
      toast.error(`Import failed: ${err?.response?.data?.message || err?.message || 'Unknown error'}`);
    }).finally(() => {
      setImportLoading(false);
    })
  };

  // Handle from date change
  const handleFromDateChange = (e) => {
    setDateRange({
      ...dateRange,
      fromDate: e.target.value
    });
  };

  // Handle to date change
  const handleToDateChange = (e) => {
    setDateRange({
      ...dateRange,
      toDate: e.target.value
    });
  };

  // Format transaction date for display
  const formatTransactionDate = (dateString) => {
    if (!dateString) return '';
    // Format from DDMMYYYYHHmmss to DD-MM-YYYY HH:MM:SS
    return moment(dateString, 'DDMMYYYYHHmmss').format('DD-MM-YYYY HH:mm:ss');
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    return moment(dateString).format('DD-MM-YYYY HH:mm:ss');
  };

  // Get status badge color
  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'processing': return 'warning';
      case 'failed': return 'danger';
      default: return 'secondary';
    }
  };

  // Excel download handler
  const handleExcelDownload = async () => {
    setExcelLoading(true);
    const params = [];
    if (transactionDate) params.push(`date=${transactionDate}`);
    if (vehicleNumber) params.push(`vehicle_number=${encodeURIComponent(vehicleNumber)}`);
    const url = `/vm/iocl/transactions/excel?${params.join('&')}`;
    get(url, { responseType: 'arraybuffer' })
      .then((res) => {
        const blob = new Blob([res], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const fileName = `IOCL_Transactions_${transactionDate}${vehicleNumber ? '_' + vehicleNumber : ''}.xlsx`;
        saveAs(blob, fileName);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.data?.message || err?.message);
      }).finally(() => {
        setExcelLoading(false);
      })
  };

  return (
    <React.Fragment>
      <ToastContainer />
      <div className="content-title-wrpper mb-3">
        <Card className="w-100">
          <Card.Header className="bg-white">
            <Tabs
              activeKey={activeTab}
              onSelect={(k) => {
                setActiveTab(k);
                setCurrentPage(1);
              }}
              className="mb-0"
            >
              <Tab eventKey="transactions" title="IOCL Transactions" />
              <Tab eventKey="imports" title="Import Logs" />
            </Tabs>
          </Card.Header>

          {activeTab === 'transactions' && (
            <>
              <Card.Body className="px-0 pt-0">
                <Row className="mx-0">
                  <Col md={6} className="p-3">
                    <Card className="mb-0">
                      <Card.Header className="py-2">
                        <span className="fw-bold">Transaction Statistics</span>
                      </Card.Header>
                      <Card.Body className="py-3">
                        <Row>
                          <Col md={6}>
                            <h5 className="fs-6 fw-bold">Total Transactions</h5>
                            <h3 className="fs-4">{stats.totalTransactions || 0}</h3>
                          </Col>
                          <Col md={6}>
                            <h5 className="fs-6 fw-bold">Latest Sync</h5>
                            <p className="mb-0">{stats.latestSync ? moment(stats.latestSync).format('DD-MM-YYYY HH:mm:ss') : 'Never'}</p>
                          </Col>
                        </Row>
                        <Row className="mt-3">
                          <Col>
                            <h5 className="fs-6 fw-bold">Transaction Types</h5>
                            <div>
                              {stats.transactionsByType && stats.transactionsByType.map((type, index) => (
                                <Badge key={index} bg="info" className="me-2 mb-2">
                                  {type.txn_type}: {type.count}
                                </Badge>
                              ))}
                            </div>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={6} className="p-3">
                    <Card className="mb-0">
                      <Card.Header className="py-2">
                        <span className="fw-bold">Sync Transactions</span>
                      </Card.Header>
                      <Card.Body className="py-3">
                        <Form>
                          <Row>
                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label className="small">From Date</Form.Label>
                                <Form.Control
                                  type="date"
                                  value={dateRange.fromDate}
                                  onChange={handleFromDateChange}
                                  max={dateRange.toDate}
                                  className="form-control-sm"
                                />
                              </Form.Group>
                            </Col>
                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label className="small">To Date</Form.Label>
                                <Form.Control
                                  type="date"
                                  value={dateRange.toDate}
                                  onChange={handleToDateChange}
                                  min={dateRange.fromDate}
                                  max={moment(dateRange.fromDate).add(7, 'days').format('YYYY-MM-DD')}
                                  className="form-control-sm"
                                />
                                <Form.Text className="text-muted small">
                                  Note: IOCL API limits date range to 7 days maximum
                                </Form.Text>
                              </Form.Group>
                            </Col>
                          </Row>
                          <div className="d-flex gap-2">
                            <Button
                              variant="success"
                              size="sm"
                              onClick={triggerSync}
                              disabled={syncLoading}
                            >
                              {syncLoading ? <Spinner size="sm" animation="border" /> : <><FiDownload className="me-1" /> Sync & Save Transactions</>}
                            </Button>
                          </div>
                        </Form>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </Card.Body>

              {/* Filters Row */}
              <Card.Body className="pb-0 pt-3">
                <Row className="mb-2">
                  <Col md={3} sm={6} xs={12} className="mb-2">
                    <InputGroup>
                      <InputGroup.Text><FiSearch /></InputGroup.Text>
                      <Form.Control
                        type="text"
                        placeholder="Filter by Vehicle Number"
                        value={vehicleNumber}
                        onChange={e => { setVehicleNumber(e.target.value); setCurrentPage(1); }}
                        className="form-control-sm"
                      />
                    </InputGroup>
                  </Col>
                  <Col md={3} sm={6} xs={12} className="mb-2">
                    <Form.Control
                      type="date"
                      value={transactionDate}
                      onChange={e => { setTransactionDate(e.target.value); setCurrentPage(1); }}
                      max={moment().format('YYYY-MM-DD')}
                      className="form-control-sm"
                    />
                  </Col>
                  <Col md={3} sm={6} xs={12} className="mb-2">
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={fetchTransactions}
                      disabled={loading}
                    >
                      {loading ? <Spinner size="sm" animation="border" /> : <><FiRefreshCw className="me-1" /> Fetch Transactions</>}
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
              {/* End Filters Row */}

              <Card.Body className="px-0 vehicle-requests-table pt-0 pb-0">
                <div className="card-header bg-white d-flex justify-content-between align-items-center py-2">
                  <span className="fw-bold">IOCL Transactions | {totalRecords ?? 0}</span>
                  <div className="d-flex gap-2">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => setShowImportModal(true)}
                    >
                      <FiUpload className="me-1" /> Import Excel
                    </Button>
                    <Button
                      variant="outline-success"
                      size="sm"
                      onClick={handleExcelDownload}
                      disabled={excelLoading}
                    >
                      {excelLoading ? <Spinner size="sm" animation="border" /> : <><FiDownload className="me-1" /> Download Excel</>}
                    </Button>
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    {loading ? (
                      <div className="text-center py-4">
                        <Spinner animation="border" />
                        <p className="mt-2">Loading transactions...</p>
                      </div>
                    ) : transactions.length === 0 ? (
                      <Alert variant="info" className="m-3">No transactions found for the selected date or vehicle</Alert>
                    ) : (
                      <div className="table-responsive">
                        <Table responsive striped hover>
                          <thead>
                            <tr>
                              <th>S.No</th>
                              <th>Vehicle No</th>
                              <th>Date</th>
                              <th>FuelTimeStamp</th>
                              <th>Terminal ID</th>
                              <th>Fuel_Merchant ID</th>
                              <th>Fuel_Merchant name</th>
                              <th>Fuel_Location</th>
                              <th>Customer ID/Card PAN</th>
                              <th>Txn Mode</th>
                              <th>Fuel_Type</th>
                              <th>RSP</th>
                              <th>Quantity</th>
                              <th>Amount</th>
                              <th>Balance</th>
                              <th>Odometer (User Entry)</th>
                              <th>Status</th>
                              <th>FCCTransactionId</th>
                              <th>Total Movement (kms)</th>
                              <th>Start Location</th>
                              <th>End Location</th>
                              <th>Start Time</th>
                              <th>End Time</th>
                              <th>Work Hours ([h]:mm:ss)</th>
                              <th>Overspeed Events</th>
                              <th>Long Halt Events</th>
                              <th>Harsh Braking Events</th>
                              <th>Door Open Events</th>
                              <th>No Data Events</th>
                              <th>Halt Duration ([h]:mm:ss)</th>
                              <th>Move Duration ([h]:mm:ss)</th>
                              <th>No Data Duration ([h]:mm:ss)</th>
                              <th>No Data Navigation Distance (kms)</th>
                              <th>Total Movement With ND Distance (kms)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {transactions.map((transaction, index) => (
                              <tr key={index}>
                                <td>{(currentPage - 1) * TABLE_DEFAULT_LIMIT + (index + 1)}</td>
                                <td>{transaction.vehicle_number || 'N/A'}</td>
                                <td>{formatTransactionDate(transaction.txn_date_time)}</td>
                                <td>{transaction.txn_date_time}</td>
                                <td>{transaction.tid}</td>
                                <td>{transaction.mid}</td>
                                <td>{transaction.merchant_name}</td>
                                <td>{transaction.location}</td>
                                <td>{transaction.card_number}</td>
                                <td>{transaction.txn_type}</td>
                                <td>{transaction.product}</td>
                                <td>{transaction.rsp}</td>
                                <td>{transaction.qty}</td>
                                <td>{transaction.amount}</td>
                                <td>{transaction.balance}</td>
                                <td>{transaction.odo_meter}</td>
                                <td>{transaction.settlement_status}</td>
                                <td>{transaction.transaction_id}</td>
                                <td>{transaction.total_movement_kms}</td>
                                <td>{transaction.start_location}</td>
                                <td>{transaction.end_location}</td>
                                <td>{transaction.start_time ? moment(transaction.start_time).format('DD-MM-YYYY HH:mm:ss') : ''}</td>
                                <td>{transaction.end_time ? moment(transaction.end_time).format('DD-MM-YYYY HH:mm:ss') : ''}</td>
                                <td>{transaction.work_hours}</td>
                                <td>{transaction.overspeed_events}</td>
                                <td>{transaction.long_halt_events}</td>
                                <td>{transaction.harsh_braking_events}</td>
                                <td>{transaction.door_open_events}</td>
                                <td>{transaction.no_data_events}</td>
                                <td>{transaction.halt_duration}</td>
                                <td>{transaction.move_duration}</td>
                                <td>{transaction.no_data_duration}</td>
                                <td>{transaction.no_data_navigation_distance}</td>
                                <td>{transaction.total_movement_with_nd_distance}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                        <PaginationInPage
                          currentPage={currentPage}
                          setCurrentPage={setCurrentPage}
                          totalRecords={totalRecords}
                          TABLE_DEFAULT_LIMIT={TABLE_DEFAULT_LIMIT}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </Card.Body>
            </>
          )}

          {activeTab === 'imports' && (
            <Card.Body className="px-0 vehicle-requests-table pt-0 pb-0">
              <div className="card-header bg-white d-flex justify-content-between align-items-center py-2">
                <span className="fw-bold">Import Logs</span>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => setShowImportModal(true)}
                >
                  <FiUpload className="me-1" /> Import Excel
                </Button>
              </div>
              <div className="row">
                <div className="col">
                  <Table responsive striped hover>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Filename</th>
                        <th>File Type</th>
                        <th>Total Records</th>
                        <th>Successful</th>
                        <th>Failed</th>
                        <th>Status</th>
                        <th>Imported By</th>
                        <th>Import Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importLogs.length === 0 ? (
                        <tr>
                          <td colSpan="9" className="text-center">No import logs found</td>
                        </tr>
                      ) : (
                        importLogs.map((log, index) => (
                          <tr key={log.id}>
                            <td>{(currentPage - 1) * TABLE_DEFAULT_LIMIT + (index + 1)}</td>
                            <td>{log.filename}</td>
                            <td>{log.file_type}</td>
                            <td>{log.total_records}</td>
                            <td>{log.successful_imports}</td>
                            <td>{log.failed_imports}</td>
                            <td>
                              <Badge bg={getStatusBadge(log.status)}>
                                {log.status}
                              </Badge>
                            </td>
                            <td>{log.imported_by_user?.name || log.imported_by}</td>
                            <td>{formatDate(log.createdAt)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                  <PaginationInPage
                    currentPage={currentPage}
                    setCurrentPage={setCurrentPage}
                    totalRecords={totalRecords}
                    TABLE_DEFAULT_LIMIT={TABLE_DEFAULT_LIMIT}
                  />
                </div>
              </div>
            </Card.Body>
          )}
        </Card>

        {/* Import Modal */}
        <Modal show={showImportModal} onHide={() => setShowImportModal(false)} backdrop="static" keyboard={false} className="request-modal">
          <Modal.Header>
            <Modal.Title>Import Vehicle Data</Modal.Title>
            <button type="button" className="btn-close" aria-label="Close" onClick={() => setShowImportModal(false)}></button>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Select CSV or Excel File</Form.Label>
                <Form.Control
                  type="file"
                  onChange={handleFileChange}
                  accept=".csv, .xlsx, .xls"
                />
                <Form.Text className="text-muted">
                  Supported formats: XLS
                </Form.Text>
              </Form.Group>
            </Form>
            <div className="text-center mt-4">
              <Button
                variant="primary"
                onClick={handleImport}
                disabled={importLoading || !selectedFile}
                className="add-request-btn"
              >
                {importLoading ? <Spinner size="sm" animation="border" /> : 'Import'}
              </Button>
            </div>
          </Modal.Body>
        </Modal>
      </div>
    </React.Fragment>
  );
};

export default IOCLReport;